/* This component can be used on its own, but also in combination with PopoverOverlay -> go
 there for more info */

import { cloneElement, isValidElement, ReactElement, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover.tsx'
import { cn } from '@/lib/utils.ts'
import Property from '@/components/common/propertyPopoverOverlay/Property.tsx'
import PropertyCommand from '@/components/common/propertyPopoverOverlay/PropertyCommand.tsx'

import { PopoverOverlayT } from '@/components/common/propertyPopoverOverlay/PopoverOverlay.tsx'
import TriggerWithCloseBtnWrapper from '@/components/common/propertyPopoverOverlay/TriggerWithCloseBtnWrapper.tsx'
import { useTranslation } from 'react-i18next'
import PropertyPopoverTrigger from '@/components/common/propertyPopoverOverlay/PropertyPopoverTrigger.tsx'
import { useHandleProperties } from '@/components/common/propertyPopoverOverlay/useHandleProperties.tsx'
import SelectFallback from '@/components/common/propertyPopoverOverlay/overlay/SelectFallback.tsx'
import PropertyGoBackNew from './PropertyGoBackNew'

/**
 * The props PropertyPopover injects into a custom trigger, plus the one it reads
 * back off the clone (`disabled`). Typing the clone keeps that read from
 * degrading to `unknown`.
 */
type CustomTriggerProps = {
  className?: string
  disabled?: boolean
  handleClick?: () => void
}

type PropertyPopoverProps = PopoverOverlayT & {
  className?: string
  /**
   * Radix align for the PopoverContent relative to the trigger.
   * Defaults to `'start'` so existing call sites are unaffected. Use
   * `'end'` when the trigger stretches full-width but the visual anchor
   * (label + chevron) sits on the right — e.g. the mobile topbar stage
   * switcher — so the popover drops down from the right edge rather than
   * flying to the left side of the screen.
   */
  align?: 'start' | 'center' | 'end'
}

const PropertyPopover = ({
  addSelectedValue,
  className,
  closeOnSelect,
  customTrigger,
  fallbackMessage,
  onClose,
  onSelectParent,
  properties,
  removeSelectedValue,
  searchLabel,
  selectAll,
  selectedValues,
  showSearch,
  XBtn,
  handleSearch,
  showAllSearchResults,
  modal,
  selectedParent,
  debug,
  removeSelectedParent,
  popoverHeader,
  align = 'start',
}: PropertyPopoverProps) => {
  const { t } = useTranslation()
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false)

  const handleOpenChange = (open: boolean) => {
    // console.log(`handleOpenChange`)
    setPopoverOpen(open)
    if (open) setCurrentProperties(properties)
    if (!open) {
      onClose && onClose()
    }
  }

  const {
    setCurrentProperties,
    currentProperties,
    handleGoBack,
    propertiesFlat,
    selectedNestedProperties,
    handleSelectParent,
    handleValueChange,
    pureProps,
  } = useHandleProperties(
    properties,
    onSelectParent,
    popoverOpen,
    selectAll,
    showAllSearchResults,
    false,
    removeSelectedParent,
  )

  // Clone the custom trigger and attach handleClick.
  //
  // `className` is MERGED here, never replaced. cloneElement overwrites exactly
  // the props it is handed, so passing a bare string used to drop whatever class
  // the call site had put on its own trigger — silently, and on desktop only
  // (Overlay.tsx, the mobile path, clones without touching className). Every
  // width, alignment and padding a caller set through `className` was therefore
  // dead here, which is why some call sites reach for the `cl` escape hatch on
  // PropertyPopoverTrigger instead.
  //
  // `h-full` is merged LAST on purpose: on a Tailwind height conflict the last
  // class wins, and `h-full` is a layout requirement of the XBtn wrapper around
  // the trigger (TriggerWithCloseBtnWrapper), not a preference of the call site.
  //
  // The className prop is omitted entirely when there is nothing to merge, so a
  // custom trigger that does not accept className (an empty <div>, a Fragment)
  // is not handed one. A non-element customTrigger falls through to the default
  // trigger below instead of throwing inside cloneElement.
  const mergedTriggerClassName = isValidElement<{ className?: string }>(customTrigger)
    ? cn(customTrigger.props.className, XBtn && 'h-full')
    : ''

  const customPopoverTrigger = isValidElement(customTrigger)
    ? cloneElement(customTrigger as ReactElement<CustomTriggerProps>, {
        handleClick: () => handleOpenChange(popoverOpen),
        ...(mergedTriggerClassName ? { className: mergedTriggerClassName } : {}),
      })
    : null

  const trigger = (
    <PopoverTrigger
      asChild={true}
      disabled={customPopoverTrigger?.props.disabled}
      onKeyDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {customPopoverTrigger ?? (
        <PropertyPopoverTrigger
          className={XBtn ? 'h-full' : ''}
          variant={XBtn ? 'ghost' : 'outline'}
          handleClick={() => handleOpenChange(popoverOpen)}
        />
      )}
    </PopoverTrigger>
  )

  return (
    <Popover modal={modal} open={popoverOpen} onOpenChange={handleOpenChange}>
      {XBtn ? (
        <TriggerWithCloseBtnWrapper
          size={XBtn.size}
          variant={XBtn.variant}
          onClick={XBtn.onClick}
          trigger={trigger}
        />
      ) : (
        trigger
      )}

      <PopoverContent
        align={align}
        className={cn(`z-[1050] flex max-w-[300px] flex-col rounded-lg p-0 pb-1`, className)}
      >
        {popoverHeader && (
          <div className={`border-b px-3 py-2 text-xs text-muted-foreground`}>{popoverHeader}</div>
        )}
        <PropertyCommand
          searchLabel={searchLabel}
          showSearch={showSearch}
          propertiesFlat={propertiesFlat}
          setCurrentProperties={setCurrentProperties}
          handleValueChange={handleValueChange}
          handleSearch={handleSearch}
          showAllSearchResults={showAllSearchResults}
          selectedParent={selectedParent}
          debug={debug}
          initialProperties={properties}
          selectedNestedProperties={selectedNestedProperties}
        >
          {pureProps.length < 1 ? (
            <SelectFallback
              className={`px-2 pb-1 pt-2`}
              fallbackMessage={fallbackMessage ?? t(`common.noDataFound`)}
            />
          ) : (
            currentProperties.map((property, index) => {
              return (
                <Property
                  key={`${property.id}_${index}`}
                  property={property}
                  setCurrentProperties={setCurrentProperties}
                  handleOpenChange={handleOpenChange}
                  closeOnSelect={closeOnSelect}
                  selectedValues={selectedValues}
                  addSelectedValue={addSelectedValue}
                  removeSelectedValue={removeSelectedValue}
                  selectAll={selectAll}
                  parent={`popover`}
                  handleSelectParent={handleSelectParent}
                />
              )
            })
          )}
          <>
            {selectedNestedProperties.length > 0 && (
              <>
                <PropertyGoBackNew handleGoBack={handleGoBack} parent={`popover`} />
              </>
            )}
          </>
        </PropertyCommand>
      </PopoverContent>
    </Popover>
  )
}

export default PropertyPopover
